import { Box, Grid } from '@material-ui/core';
import { getLoanStatusesByLoanId, Ticketing } from '@nemuru-eng/js-api-client';

import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useActivitySummary } from '@/components/widgets/CustomerSupport/components/CustomerSupportDialog/components/CustomerSupportHeader/hooks/useActivitySummary';
import { ActivityCardVariant } from '@/components/widgets/CustomerSupport/components/CustomerSupportDialog/components/CustomerSupportHeader/types/ActivityCardVariant';
import { useCalculateTime } from '@/components/widgets/CustomerSupport/hooks/useCalculateTime';
import { useCache } from '@/hooks/requests/useCache';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface ActivitySummaryCardProps {
  variant: ActivityCardVariant;
  ticketing: Ticketing;
  loanId: string;
}

export const ActivitySummaryCard = ({ variant, ticketing, loanId }: ActivitySummaryCardProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const { data: loanStatuses } = useCache(getLoanStatusesByLoanId, { loanId }, { revalidateOnMount: false });

  const { calculateTimePassed } = useCalculateTime();
  const { findLoanEndingSupportStatus, calculateNumberOfActions } = useActivitySummary();

  if (!loanStatuses) return <></>;

  const loanEndingSupportStatus = findLoanEndingSupportStatus(loanStatuses);

  const dateForCalculation = loanEndingSupportStatus ? loanEndingSupportStatus[0].updatedAt : undefined;

  const ActivityCardParams = {
    [ActivityCardVariant.ACTIONS_AMOUNT]: {
      iconName: 'support_agent',
      titleTextId: 'TOTAL_ACTIONS',
      bodyValue: calculateNumberOfActions(ticketing.comments),
    },
    [ActivityCardVariant.LAST_ACTION]: {
      iconName: 'schedule',
      titleTextId: 'TIME_PASSED_SINCE_LAST_ACTION',
      bodyValue: calculateTimePassed({ startDateStr: ticketing.content.updatedAt, endDateStr: dateForCalculation }),
    },
    [ActivityCardVariant.ACTIONS_TIME]: {
      iconName: 'timelapse',
      titleTextId: 'TOTAL_ACTIONS_TIME',
      bodyValue: calculateTimePassed({ startDateStr: ticketing.content.createdAt, endDateStr: dateForCalculation }),
    },
  };

  return (
    <Box border={`1px solid ${palette.grey[200]}`} borderRadius={customBorderRadius.paper} padding={2}>
      <Grid container spacing={0} direction={'row'}>
        <Grid item xs={2} style={{ textAlign: 'center' }}>
          <i className="material-icons-outlined" style={{ fontSize: '24px', color: palette.secondary.main }}>
            {ActivityCardParams[variant].iconName}
          </i>
        </Grid>
        <Grid item xs={10}>
          <Box display={'flex'} justifyContent={'space-between'} flexDirection={'column'}>
            <Font variant={'h5'} color={'secondary'} style={{ marginTop: '2px' }}>
              <Text strong tId={ActivityCardParams[variant].titleTextId} />
            </Font>
            <Font variant={'h3'} color={'textSecondary'} style={{ paddingBlock: '4px' }}>
              {ActivityCardParams[variant].bodyValue}
            </Font>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
